import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Pencil, Check, X, Sparkles, Loader2, Video, Mail as MailIcon, Camera, Shield } from "lucide-react";
import { ProfileStrengthFull } from "../components/ProfileStrength";

function EditableSection({ label, field, value, onSave, improving }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);

  if (!editing) {
    return (
      <div className="group" data-testid={`section-${field}`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider">{label}</div>
          <button onClick={() => setEditing(true)} className="text-[#666] hover:text-[#F5F5F5] transition-colors p-0.5 opacity-0 group-hover:opacity-100" data-testid={`edit-${field}`}><Pencil size={10} /></button>
        </div>
        <p className="text-[11px] text-[#999] leading-relaxed min-h-[16px] cursor-pointer hover:text-[#ccc] transition-colors" onClick={() => setEditing(true)}>
          {value || <span className="italic text-[#444]">Click to add...</span>}
        </p>
      </div>
    );
  }

  return (
    <div data-testid={`section-${field}-editing`}>
      <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-1.5">{label}</div>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full bg-[#1A1A1A] border border-[#D4AF37]/30 text-[#E5E5E5] text-[11px] leading-relaxed p-2.5 rounded-sm resize-none focus:outline-none focus:border-[#D4AF37]/60" rows={Math.max(3, (draft || "").split("\n").length + 1)} autoFocus data-testid={`textarea-${field}`} />
      <div className="flex gap-1.5 mt-1.5">
        <button onClick={() => { onSave(field, draft); setEditing(false); }} className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[9px] px-3 py-1 rounded-sm hover:bg-[#C5A059] transition-colors flex items-center gap-1" data-testid={`save-${field}`}><Check size={10} /> SAVE</button>
        <button onClick={() => { setDraft(value || ""); setEditing(false); }} className="bg-[#1A1A1A] border border-[#262626] text-[#999] font-bold uppercase tracking-wider text-[9px] px-3 py-1 rounded-sm hover:text-[#F5F5F5] transition-colors flex items-center gap-1" data-testid={`cancel-${field}`}><X size={10} /> CANCEL</button>
      </div>
    </div>
  );
}

export default function AdminProfileEdit() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [videoLink, setVideoLink] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/profiles/${userId}/edit-data`);
        if (data.profile) {
          setProfile(data.profile);
          setStrength(data.strength);
          setVideoLink(data.profile.highlight_video || "");
          setContactEmail(data.session?.modules?.contact?.contact_email || "");
        } else {
          toast.error("Profile not found");
          navigate("/admin");
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Access denied");
        navigate("/admin");
      }
      setLoading(false);
    };
    load();
  }, [userId, navigate]);

  const refreshData = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/profiles/${userId}/edit-data`);
      setProfile(data.profile);
      setStrength(data.strength);
    } catch {}
  }, [userId]);

  const handleSectionSave = async (field, newValue) => {
    setSaving(true);
    try {
      await api.put(`/admin/profiles/${userId}/save`, { edited_fields: { [field]: newValue } });
      await refreshData();
      toast.success(`${field.replace(/_/g, " ")} updated`);
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const handleSaveMedia = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/profiles/${userId}/save-media`, { highlight_video: videoLink, contact_email: contactEmail });
      await refreshData();
      toast.success("Media saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (loading || !profile) {
    return (<div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  const gen = profile.generated_profile || {};
  const edited = profile.edited_fields || {};
  const get = (key) => edited[key] || gen[key] || "";

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="admin-profile-edit">
      {/* Admin banner */}
      <div className="bg-[#1A0A0A] border-b border-red-500/20 px-4 py-2.5" data-testid="admin-edit-banner">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-red-400" />
            <span className="text-xs text-red-400 font-bold uppercase tracking-wider">ADMIN EDIT MODE</span>
            <span className="text-[10px] text-[#999] ml-2">Editing: {profile.name || "Unknown"} (ID: {userId})</span>
          </div>
          <Link to="/admin" className="bg-[#262626] text-[#E5E5E5] font-bold uppercase tracking-wider text-[9px] px-4 py-1.5 rounded-sm hover:bg-[#333] transition-colors flex items-center gap-1" data-testid="exit-admin-edit">
            <ArrowLeft size={10} /> EXIT ADMIN EDIT
          </Link>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-[#1A1A1A] px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-sm font-bold tracking-wider uppercase text-[#F5F5F5]">{profile.name}</h1>
            <p className="text-[10px] text-[#555]">Admin editing &middot; {profile.status || "draft"} &middot; {profile.slug ? `/player/${profile.slug}` : "no slug"}</p>
          </div>
          <div className="flex gap-2">
            {profile.slug && (
              <a href={`/player/${profile.slug}`} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid="admin-preview-profile"><Eye size={12} /> PREVIEW</a>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          {/* Main editable sections */}
          <div className="space-y-5">
            <EditableSection label="Headline" field="headline" value={get("headline")} onSave={handleSectionSave} />
            <div className="border-t border-[#1A1A1A]" />
            <EditableSection label="Overview" field="overview" value={get("overview")} onSave={handleSectionSave} />
            <div className="border-t border-[#1A1A1A]" />
            <EditableSection label="Playing Style" field="playing_style" value={get("playing_style")} onSave={handleSectionSave} />
            <div className="border-t border-[#1A1A1A]" />

            {gen.tactical_profile && (
              <>
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">TACTICAL PROFILE</div>
                <div className="space-y-3">
                  {gen.tactical_profile.in_possession && <EditableSection label="In Possession" field="tactical_in_possession" value={edited.tactical_in_possession || gen.tactical_profile.in_possession} onSave={handleSectionSave} />}
                  {gen.tactical_profile.out_of_possession && <EditableSection label="Out of Possession" field="tactical_out_of_possession" value={edited.tactical_out_of_possession || gen.tactical_profile.out_of_possession} onSave={handleSectionSave} />}
                  {gen.tactical_profile.transitions && <EditableSection label="Transitions" field="tactical_transitions" value={edited.tactical_transitions || gen.tactical_profile.transitions} onSave={handleSectionSave} />}
                </div>
                <div className="border-t border-[#1A1A1A]" />
              </>
            )}

            <EditableSection label="Development Focus" field="development_focus" value={get("development_focus")} onSave={handleSectionSave} />
            <div className="border-t border-[#1A1A1A]" />
            <EditableSection label="Experience Summary" field="experience_summary" value={get("experience_summary")} onSave={handleSectionSave} />

            {/* Media */}
            <div className="border-t border-[#1A1A1A] pt-4">
              <div className="text-[9px] font-bold text-[#F5F5F5] uppercase tracking-wider mb-3">MEDIA & CONTACT</div>
              <div className="space-y-2.5">
                <div>
                  <label className="text-[8px] font-bold text-[#666] uppercase tracking-wider mb-1 flex items-center gap-1"><Video size={9} />HIGHLIGHT VIDEO</label>
                  <input type="url" value={videoLink} onChange={(e) => setVideoLink(e.target.value)} className="input-dark text-xs py-1.5" placeholder="YouTube or Vimeo URL" data-testid="admin-edit-video" />
                </div>
                <div>
                  <label className="text-[8px] font-bold text-[#666] uppercase tracking-wider mb-1 flex items-center gap-1"><MailIcon size={9} />CONTACT EMAIL</label>
                  <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="input-dark text-xs py-1.5" placeholder="Contact email" data-testid="admin-edit-contact" />
                </div>
                <button onClick={handleSaveMedia} disabled={saving} className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[9px] w-full py-1.5 rounded-sm hover:bg-[#C5A059] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="admin-save-media">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} SAVE MEDIA
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar — strength + info */}
          <div className="space-y-5">
            {strength && (
              <div className="card-dark p-4" data-testid="admin-edit-strength">
                <ProfileStrengthFull strength={strength} showActions={false} />
              </div>
            )}
            <div className="card-dark p-4">
              <div className="text-[9px] font-bold text-[#F5F5F5] uppercase tracking-wider mb-3">PROFILE INFO</div>
              <div className="space-y-2 text-[10px]">
                <div className="flex justify-between"><span className="text-[#666]">Status</span><span className={profile.status === "published" ? "text-green-400 font-bold" : "text-yellow-400 font-bold"}>{profile.status || "draft"}</span></div>
                <div className="flex justify-between"><span className="text-[#666]">Verified</span><span className={profile.is_reviewed ? "text-[#D4AF37]" : "text-[#666]"}>{profile.is_reviewed ? "Yes" : "No"}</span></div>
                <div className="flex justify-between"><span className="text-[#666]">Featured</span><span className={profile.is_featured ? "text-[#D4AF37]" : "text-[#666]"}>{profile.is_featured ? "Yes" : "No"}</span></div>
                <div className="flex justify-between"><span className="text-[#666]">Circulation</span><span className={profile.is_circulation_candidate ? "text-purple-400" : "text-[#666]"}>{profile.is_circulation_candidate ? "Yes" : "No"}</span></div>
                {profile.slug && <div className="flex justify-between"><span className="text-[#666]">Slug</span><span className="text-[#ccc]">{profile.slug}</span></div>}
                <div className="flex justify-between"><span className="text-[#666]">Views</span><span className="text-[#ccc]">{profile.view_count || 0}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
